import React, { FC, useMemo } from 'react';
import usePageTitle from 'hooks/usePageTitle';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { IExtendedPlot, IPlot } from 'FeatureModules/Site/types/plot.types';
import { GET_PLOT_OVERVIEW } from 'graphql/secure/queries/plot.queries';
import Loader from 'Components/Forms/Loader';
import OverviewHeader from 'Components/OverviewHeader/OverviewHeader';
import { Grid, Box, styled, Link } from '@mui/material';
import { ThemeTypography, ThemeButton, ItemCardSmall } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import FarmPlots from 'FeatureModules/Site/Components/FarmPlots';
import DocumentIntroTitle from 'FeatureModules/Document/Components/DocumentIntroTitle';
import { Link as RouterLink } from 'react-router-dom';
import PlotComment from 'FeatureModules/Site/Components/PlotComment';
import useDialog from 'hooks/useDialog';
import { IGraphQlConnection } from 'types/core.types';
import { IDocument } from 'FeatureModules/Document/types/document.types';
import { removeGraphConnections } from 'utils/graphql.utils';
import { useNavigate } from 'react-router-dom';
import { Booleanish } from 'types/booleanish.types';
import { formatAndRoundNumber } from 'utils/number.utils';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import PageSubTitle from 'Components/PageSubTitle';

const ResizedIcon = styled(Icon)({
  svg: {
    width: '100px !important',
    height: '100px !important',
  },
});

const LinkContainer = styled(Box)(({ theme }) => ({
  a: {
    textDecoration: 'none',
  },
}));

const PlotOverview: FC = () => {
  usePageTitle('Plot overview');
  const { plotId } = useParams();
  const { openDialog } = useDialog();
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<{
    plot: IExtendedPlot;
    sharedDocuments: IGraphQlConnection<IDocument>;
    documents: IGraphQlConnection<IDocument>;
  }>(GET_PLOT_OVERVIEW, {
    skip: !plotId,
    variables: {
      id: plotId as string,
    },
  });

  const plot = data?.plot;
  const documents = useMemo(
    () => (data?.documents ? removeGraphConnections(data.documents) : []),
    [data?.documents]
  );
  const sharedDocuments = useMemo(
    () => (data?.sharedDocuments ? removeGraphConnections(data.sharedDocuments) : []),
    [data?.sharedDocuments]
  );

  const handleEditPlot = () => {
    openDialog({
      type: 'ADD_EDIT_FARM_PLOT',
      props: {
        plot: plot as IPlot,
      },
    });
  };

  const handleClickPartner = (partnerId: string) => {
    navigate(`/partners/${partnerId}`);
  };

  if (loading) return <Loader />;
  if (error || !plot) return <ErrorState action={undefined} />;

  return (
    <PageContainer>
      <PageTitle
        title="Plot overview"
        titleExtension={`${plot?.title ? plot.title : 'Unnamed Plot'} ${
          plot.farm ? ` (${plot.farm.title})` : ''
        }`}
        goBackLabel="Site overview"
        enableAutoGoBackHistoryButton
        goBackUrl={`/sites/${plot?.farmId}`}
      />
      <Box mt={3} />
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <OverviewHeader>
            <Box display="flex" gap={2} alignItems="center" ml={-2}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={130}
                height={130}
              >
                <ResizedIcon name="plot-placeholder" color="blue-5" size="huge" />
              </Box>

              <Box
                display="flex"
                alignItems="flex-start"
                flexDirection="column"
                height="100%"
                justifyContent="space-evenly"
              >
                <FlexBox>
                  <Box mb={1}>
                    <ThemeTypography variant="TITLE_SMALL" autoOverflow maxWidth={550}>
                      {plot.externalId ? plot.externalId : 'No id'}
                    </ThemeTypography>
                  </Box>

                  {plot.title && (
                    <FlexBox ml={2}>
                      <ThemeTypography variant="TITLE_EXTRA_SMALL" color="GRAY_60">
                        |
                      </ThemeTypography>
                      <Box mr={1} />
                      <ThemeTypography
                        variant="TITLE_EXTRA_SMALL"
                        autoOverflow
                        maxWidth={200}
                        color="GRAY_60"
                      >
                        {plot.title}
                      </ThemeTypography>
                    </FlexBox>
                  )}
                </FlexBox>

                <FlexBox mb={1.5} gap={1}>
                  <FlexBox gap={0.5}>
                    <Icon name="tag-filled" color="gray-80" size="small" />
                    <ThemeTypography variant="BODY_MEDIUM_BOLD" color="GRAY_80">
                      Farm plot
                    </ThemeTypography>
                  </FlexBox>

                  <FlexBox gap={0.5}>
                    <Icon name="farm" size="small" color="gray-80" />
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                      {formatAndRoundNumber(plot.sizeHa || 0, 2)} ha
                    </ThemeTypography>
                  </FlexBox>

                  <FlexBox gap={0.5}>
                    <Icon name="location" size="small" color="gray-80" />
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                      {plot.farm.title}
                    </ThemeTypography>
                  </FlexBox>

                  <FlexBox gap={0.5}>
                    <Icon name="raw-material" size="small" color="gray-80" />
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                      {plot.farm.rawMaterial.title}
                    </ThemeTypography>
                  </FlexBox>

                  <FlexBox
                    clickable={Booleanish(true)}
                    gap={0.5}
                    onClick={() =>
                      plot.farm.ownedBy?.id && handleClickPartner(plot.farm.ownedBy.id)
                    }
                  >
                    <Icon name="partner" size="small" color="gray-80" />
                    <ThemeTypography variant="LABEL_INPUT" color="GRAY_80">
                      {plot.farm.ownedBy?.name}
                    </ThemeTypography>
                  </FlexBox>
                </FlexBox>

                <FlexBox gap={2}>
                  <ThemeButton
                    startIcon={<Icon name="edit" color="WHITE" />}
                    size="large"
                    color="PRIMARY"
                    loading={false}
                    onClick={() => handleEditPlot()}
                  >
                    Edit plot
                  </ThemeButton>
                </FlexBox>
              </Box>
            </Box>
          </OverviewHeader>
        </Grid>
        <Grid item xs={6}>
          <Box flex={1} minHeight={400}>
            <PageSubTitle title="Plot cultivation area" spaceBetween={false} mb={2} />
            {/* 38px from the SubTitle header */}
            <Box height="calc(100% - 38px)" width="100%">
              <FarmPlots minHeight={350} plots={[plot]} mode="single-plot-edit" />
            </Box>
          </Box>
        </Grid>

        {documents.length > 0 ? (
          <Grid item xs={6}>
            <PageSubTitle
              title="Plot documents"
              visibleItems={documents.length}
              totalItems={data.documents.count}
              mb={2}
            >
              <LinkContainer display="flex" alignItems="center" gap={2}>
                <Link component={RouterLink} to="documents" color="secondary" className="small">
                  See all documents
                </Link>
              </LinkContainer>
            </PageSubTitle>
            {documents.map(document => (
              <ItemCardSmall key={document.id}>
                <DocumentIntroTitle document={document} showOpenInNewTab />
              </ItemCardSmall>
            ))}
          </Grid>
        ) : undefined}

        {sharedDocuments.length > 0 ? (
          <Grid item xs={6}>
            <PageSubTitle
              title="Shared plot documents"
              visibleItems={sharedDocuments.length}
              totalItems={data.sharedDocuments.count}
              mb={2}
            >
              <LinkContainer display="flex" alignItems="center" gap={2}>
                <Link
                  component={RouterLink}
                  to="documents/shared"
                  color="secondary"
                  className="small"
                >
                  See all shared documents
                </Link>
              </LinkContainer>
            </PageSubTitle>
            {sharedDocuments.map(document => (
              <ItemCardSmall key={document.id}>
                <DocumentIntroTitle document={document} showOpenInNewTab />
              </ItemCardSmall>
            ))}
          </Grid>
        ) : undefined}

        {plot.comments.edges.length > 0 ? (
          <Grid item xs={12}>
            <PageSubTitle title="Plot comments" mb={2} />
            {plot.comments.edges.slice(0, 4).map(({ node }) => (
              <PlotComment key={node.id} plotComment={node} />
            ))}
          </Grid>
        ) : undefined}
      </Grid>
    </PageContainer>
  );
};

export default PlotOverview;
